import ModalViewModel from '../../../infra/ModalViewModel'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { Id } from '../../../types/definitions'
import Workspace from '../../../iam/models/Workspace'
import RoleStore from '../../../iam/RoleStore'
import Role from '../../../iam/models/Role'
import { AlertFactory, AlertState } from '../../../alerts/Alert'
import MemberStore from '../../../iam/MemberStore'
import { func, validationContext } from 'validx'
import { task } from 'mobx-task'
import { isNotNil } from '../../../utils/object-util'
import { SearchableUser } from '../../../iam/models/SearchableUser'

export class EditUserRoleDialogViewModel extends ModalViewModel {
  @observable
  private _selectedUserIds: Set<Id> = new Set()

  @observable
  availableRoles: Array<Role> = []

  @observable
  selectedRole?: Role = undefined

  @observable
  selectedUser?: SearchableUser = undefined

  @observable
  workspace: Workspace = Workspace.none

  validation = validationContext(this, {
    selectedRole: [
      func((selected) => isNotNil(selected.value) || 'You must select a role.'),
    ],
  })

  constructor(
    private roleStore: RoleStore,
    private memberStore: MemberStore,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get selectedUserIds() {
    return Array.from(this._selectedUserIds)
  }

  @action
  setSelectedUser(user?: SearchableUser) {
    this.selectedUser = user
  }

  @action
  setWorkspace(workspace: Workspace) {
    this.workspace = workspace
  }

  @action.bound
  setSelectedRole(role?: Role) {
    this.selectedRole = role
  }

  @action
  setRoles(roles: Array<Role>) {
    this.availableRoles = roles
  }

  @action
  setSelectedUserIds(selectedUserIds: Set<Id>) {
    this._selectedUserIds = selectedUserIds
  }

  async showWithUser(user: SearchableUser, workspace: Workspace) {
    this.setSelectedUser(user)
    this.setWorkspace(workspace)

    this.setSelectedUserIds(new Set([user.publicId]))

    await Promise.all([this.fetchRoles(), this.fetchMember(user.publicId)])

    this.show()
  }

  async open(selectedUserIds: Set<Id>, workspace: Workspace) {
    this.setSelectedUserIds(selectedUserIds)
    this.setWorkspace(workspace)

    await this.fetchRoles()

    this.show()
  }

  @override
  close() {
    this.setSelectedRole()
    this.setSelectedUser()
    this.setWorkspace(Workspace.none)
    this.validation.reset()
    super.close()
  }

  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const alert = this.newAlert()

    alert
      .withState(AlertState.Pending)
      .saying('Updating role')
      .autoDismiss()
      .show()

    try {
      await Promise.all(
        this.selectedUserIds.map((userId) =>
          this.memberStore.addRole(
            Id.assert(this.workspace!.id),
            Id.assert(userId),
            Id.assert(this.selectedRole!.id),
          ),
        ),
      )

      alert
        .withState(AlertState.Success)
        .saying('Success!', 'Role updated successfully!')
        .autoDismiss()
        .show()

      this.close()
    } catch (e) {
      console.error(e)

      alert.fromError(e).autoDismiss()
    }
  })

  async fetchRoles() {
    if (!isNotNil(this.workspace)) {
      return
    }

    const roles = await this.roleStore.find({
      workspace_id: Id.assert(this.workspace.id),
    })

    this.setRoles(roles)
  }

  async fetchMember(userPublicId: Id) {
    if (this.selectedUserIds.length !== 1) {
      return
    }

    const member = await this.memberStore.fetchByUserPublicId({
      workspace_id: Id.assert(this.workspace!.id),
      user_public_id: Id.assert(userPublicId),
    })

    if (member?.roles.length) {
      this.setSelectedRole(member.roles[0])
    }
  }
}
